import { useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { format, parseISO, addDays } from 'date-fns';
import { ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * `<CheaperAlternatives>` — Google Flights-style "Travel [dates] for
 * [price]" callout that surfaces the single cheapest nearby-date
 * option above the results list, with a "Change dates" action that
 * expands a horizontal date strip (departure date → cheapest price)
 * so the customer can shop other dates inline.
 *
 * Two data sources, both already flowing through the app:
 * - `alternatives` — the `cheaper_alternatives` array from the search
 *   response (top-N nearby-date pairs cheaper than the searched dates).
 *   We show `alternatives[0]` (the cheapest) as the headline card.
 * - `datePrices` — a `{ 'YYYY-MM-DD': price }` map from the
 *   `/api/flight-calendar-prices` calendar (via `useFlightCalendarPrices`
 *   in the parent). Lazy-loaded: the parent only fetches it once the
 *   customer opens the strip, signalled by `onToggleDates(true)`.
 *
 * Design notes:
 * - Render nothing when there are no alternatives so the results
 *   layout doesn't reserve space for an empty callout.
 * - The strip is a 1-D calendar (cheapest price per departure date),
 *   not a full 2-D grid — SearchAPI's provider only returns
 *   cheapest-per-date, so a true outbound×return matrix isn't
 *   available at reasonable cost. Each cell's return date is derived
 *   from `tripLengthDays` to match the round-trip the customer is
 *   shopping.
 * - Chrome mirrors `getOfferCardShellClass` in FlightOfferCard.jsx
 *   (hairline inset borders + vertical white gradient + soft drop
 *   shadow) so the callout reads as a peer of the result cards, with
 *   a faint emerald aura marking it as the "savings" surface.
 */

const CARD_SHELL = cn(
  'relative backdrop-blur-md border-[0.5px] border-white/5 rounded-2xl overflow-hidden transition-all duration-200',
  'bg-[linear-gradient(0deg,rgba(255,255,255,0.075)_0%,rgba(255,255,255,0.012)_35%,rgba(255,255,255,0)_100%)]',
  'shadow-[inset_0_0.5px_0_0_rgba(0,0,0,0.58),inset_0_-0.5px_0_0_rgba(255,255,255,0.2),inset_0_0_0_0.5px_rgba(255,255,255,0.1),0_4px_16px_rgba(0,0,0,0.22)]',
);

function formatShortRange(depYMD, retYMD) {
  try {
    const dep = parseISO(depYMD);
    if (!retYMD) return format(dep, 'MMM d');
    const ret = parseISO(retYMD);
    // If departure and return share the same month, elide the second
    // month name — "Aug 23 – 30" instead of "Aug 23 – Aug 30".
    if (format(dep, 'MMM') === format(ret, 'MMM')) {
      return `${format(dep, 'MMM d')} – ${format(ret, 'd')}`;
    }
    return `${format(dep, 'MMM d')} – ${format(ret, 'MMM d')}`;
  } catch {
    return retYMD ? `${depYMD} – ${retYMD}` : depYMD;
  }
}

function formatPrice(price, currency = 'USD') {
  const n = Number(price);
  if (!Number.isFinite(n)) return '';
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `$${Math.round(n)}`;
  }
}

/**
 * Horizontal, scrollable strip of departure-date cells. Fed the raw
 * `{ 'YYYY-MM-DD': price }` map; sorts ascending by date, marks the
 * cheapest date in emerald and the currently-searched date with a
 * ring, and calls `onPick({ from, to, price })` on click (return date
 * = departure + `tripLengthDays`, or null for one-way).
 */
function DateStrip({
  datePrices,
  loading,
  tripLengthDays,
  currentDeparture,
  currency,
  onPick,
}) {
  const { rows, cheapestDate } = useMemo(() => {
    const entries = Object.entries(datePrices || {})
      .map(([date, price]) => ({ date, price: Number(price) }))
      .filter((r) => Number.isFinite(r.price) && r.price > 0)
      .sort((a, b) => a.date.localeCompare(b.date));
    if (entries.length === 0) return { rows: [], cheapestDate: null };
    const cheapest = entries.reduce((m, r) => (r.price < m.price ? r : m), entries[0]);
    return { rows: entries, cheapestDate: cheapest.date };
  }, [datePrices]);

  if (loading && rows.length === 0) {
    return (
      <div className="flex gap-2 overflow-hidden">
        {Array.from({ length: 7 }).map((_, i) => (
          <div
            key={i}
            className="h-[70px] w-[72px] flex-none rounded-xl bg-white/[0.03] border-[0.5px] border-white/10 animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="text-sm text-white/60 font-normal">
        No nearby-date prices available for this route right now.
      </div>
    );
  }

  const isRoundTrip = Number(tripLengthDays) > 0;

  return (
    <div className="-mx-1 px-1 flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
      {rows.map(({ date, price }) => {
        const isCheapest = date === cheapestDate;
        const isCurrent = date === currentDeparture;
        let dep;
        try {
          dep = parseISO(date);
        } catch {
          return null;
        }
        return (
          <button
            key={date}
            type="button"
            onClick={() => {
              try {
                const from = parseISO(date);
                const to = isRoundTrip ? addDays(from, Number(tripLengthDays)) : null;
                if (isNaN(from?.getTime?.())) return;
                onPick?.({ from, to, price });
              } catch {
                // Swallow — malformed cell, don't crash the page.
              }
            }}
            className={cn(
              'flex-none flex flex-col items-center justify-center gap-0.5',
              'min-w-[72px] rounded-xl px-3 py-2',
              'border-[0.5px] transition-all duration-200',
              'focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
              isCheapest
                ? 'bg-emerald-400/[0.08] border-emerald-400/30 hover:bg-emerald-400/[0.14] shadow-[0_2px_10px_rgba(16,185,129,0.12)]'
                : 'bg-white/[0.03] border-white/10 hover:bg-white/[0.07] hover:border-white/20',
              isCurrent && !isCheapest && 'ring-1 ring-white/25',
            )}
          >
            <span className="text-[9px] uppercase tracking-[0.18em] text-white/35 font-normal">
              {format(dep, 'EEE')}
            </span>
            <span className="text-[13px] text-white/90 tabular-nums leading-tight font-normal">
              {format(dep, 'MMM d')}
            </span>
            <span
              className={cn(
                'text-[13px] tabular-nums leading-tight font-medium',
                isCheapest ? 'text-emerald-300' : 'text-white/60',
              )}
            >
              {formatPrice(price, currency)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

export default function CheaperAlternatives({
  alternatives,
  currency = 'USD',
  onSelectDates,
  datePrices,
  datePricesLoading = false,
  tripLengthDays = 0,
  currentDeparture = null,
  open = false,
  onToggleDates,
  isLoading, // eslint-disable-line no-unused-vars
  className,
}) {
  const list = Array.isArray(alternatives) ? alternatives : [];
  // Alternatives arrive sorted ascending by price, so [0] is the
  // cheapest — that's the one we headline, Google-Flights-style.
  const cheapest = list[0];
  if (!cheapest) return null;

  // Controlled expand: the parent owns `open` (same flag that gates the
  // calendar-price fetch feeding the strip), so the visual state and the
  // data-fetch gate can never drift out of sync — e.g. showing an open
  // strip with stale prices after a new search.
  const toggle = () => {
    // Tell the parent to start (or stop) fetching the calendar prices
    // that feed the strip — lazy so we don't spend a call unless the
    // customer actually wants to shop dates.
    if (typeof onToggleDates === 'function') onToggleDates(!open);
  };

  const handleSelect = ({ from, to, price }) => {
    if (typeof onSelectDates !== 'function') return;
    onSelectDates({ from, to, price });
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.25 }}
      className={cn(CARD_SHELL, 'mb-4', className)}
    >
      {/* Faint emerald aura — quiet "savings" signal that separates
          this card from the neutral result cards below it. */}
      <div
        aria-hidden="true"
        className="absolute -top-16 -left-10 h-44 w-44 rounded-full bg-emerald-400/[0.07] blur-3xl pointer-events-none"
      />

      <div className="relative px-4 py-3.5 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-[0.22em] text-emerald-300/70 font-normal">
              Cheaper dates
            </p>
            {/* Headline, matching Google Flights' "Travel [dates] for
                [price]" — the single cheapest nearby-date option. */}
            <p className="mt-0.5 text-[17px] sm:text-lg text-white font-light tracking-tight leading-snug">
              Travel{' '}
              <span className="font-normal tabular-nums">
                {formatShortRange(cheapest.departure, cheapest.return)}
              </span>{' '}
              for{' '}
              <span className="text-emerald-300 font-medium tabular-nums">
                {formatPrice(cheapest.price, currency)}
              </span>
            </p>
          </div>

          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className={cn(
              'inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full',
              'border border-white/10 bg-white/[0.03] text-[13px] font-normal',
              'transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40',
              open
                ? 'text-emerald-300 border-emerald-400/25 bg-emerald-400/[0.06]'
                : 'text-white/75 hover:text-white hover:bg-white/[0.08]',
            )}
          >
            Change dates
            <ChevronDown
              className={cn('h-3.5 w-3.5 transition-transform duration-200', open && 'rotate-180')}
            />
          </button>
        </div>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="strip"
              layout
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-3 pt-3 border-t border-white/[0.06]">
                <DateStrip
                  datePrices={datePrices}
                  loading={datePricesLoading}
                  tripLengthDays={tripLengthDays}
                  currentDeparture={currentDeparture}
                  currency={currency}
                  onPick={handleSelect}
                />
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
}
